import map from 'lodash/map'
import omit from 'lodash/omit'
import createSagaMiddleware from 'redux-saga'
import * as sagaEffects from 'redux-saga/effects'
import * as models from '../models'
import createPromiseMiddleware from './createPromiseMiddleware'
import error from './error'
import loading from './loading'
import loadModel from './loadModel'
import * as authModel from '@/redux/models/auth'
import * as sessionModel from '@/redux/models/session'

import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { createBrowserHistory } from 'history'
import { createReduxHistoryContext } from 'redux-first-history'

import { persistReducer, persistStore, createTransform } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { Env } from '../../config/Env'

const authTransform = createTransform(
  null,
  state => {
    // Destructure object to be blacklisted
    const { token } = state
    let newState = omit(state, ['toVerify'])

    // Do not black list these if token exists
    if (!token) {
      newState = omit(newState, ['isVerifying', 'needChgPwd'])
    }

    return { ...authModel.INITIAL_STATE, ...newState }
  },
  { whitelist: ['auth'] },
)

const sessionTransform = createTransform(
  null,
  state => ({ ...sessionModel.INITIAL_STATE, watchHistory: state.watchHistory }),
  { whitelist: ['session'] },
)

const persistConfig = {
  key: 'dscRoot',
  storage,
  whitelist: ['auth', 'profile', 'session'],
  transforms: [authTransform, sessionTransform],
}

const { all } = sagaEffects

const genEffects = (effectLbl, effect, operation) =>
  /* eslint-disable func-names */
  function* () {
    function* generationEffect(e) {
      const { _resolve, _reject } = e
      try {
        yield sagaEffects.put({
          type: 'loading/updateState',
          payload: {
            [e.type]: true,
          },
        })

        const ret = yield effect(e, sagaEffects)

        yield sagaEffects.put({
          type: 'loading/updateState',
          payload: {
            [e.type]: false,
          },
        })
        _resolve(ret)
      } catch (ee) {
        _reject(ee)
      }
    }

    let type = 'takeEvery'

    if (operation) {
      type = operation.type
    }

    if (type === 'throttle') {
      if (operation && operation.ms) {
        yield sagaEffects.throttle(operation.ms, effectLbl, generationEffect)
      } else {
        yield sagaEffects.takeEvery(effectLbl, generationEffect)
      }
    } else if (type === 'takeLatest') {
      yield sagaEffects.takeLatest(effectLbl, generationEffect)
    } else {
      yield sagaEffects.takeEvery(effectLbl, generationEffect)
    }
  }

const getEffects = () => {
  let effectMain = []
  const lblList = []
  const reducerMain = {}

  const modules = {
    ...models,
    error,
    loading,
  }

  map(modules, model => {
    const { namespace, reducers, effects } = loadModel(model)

    if (reducers) {
      reducerMain[namespace] = reducers
    }

    if (effects) {
      effectMain = [
        ...effectMain,
        ...map(effects, eff => {
          const { effectLbl, effect } = eff

          lblList.push(effectLbl)

          if (effect && Array.isArray(effect) && effect.length >= 2) {
            return genEffects(effectLbl, effect[0], effect[1])()
          }

          return genEffects(effectLbl, effect)()
        }),
      ]
    }
  })

  return { effectMain, effectsList: lblList, reducerMain }
}

const genRootSaga = effs =>
  function* rootSaga() {
    yield all(effs)
  }

const { effectMain, effectsList, reducerMain } = getEffects()

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
  reduxTravelling: Env.isDev(),
  savePreviousLocations: 1,
})

reducerMain.router = routerReducer

const promiseMiddleware = createPromiseMiddleware(effectsList)

const sagaMiddleware = createSagaMiddleware()

export const setupMiddlewares = m => m

/* eslint-disable no-underscore-dangle */
// const composeEnhancers =
//   process.env.NODE_ENV !== 'production' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
//     ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true, maxAge: 30 })
//     : compose

// const middlewares = setupMiddlewares([promiseMiddleware, sagaMiddleware])

// const enhancer = composeEnhancers(applyMiddleware(...middlewares))
const persistedReducer = persistReducer(persistConfig, combineReducers(reducerMain))

const makeStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    devTools: Env.isDev(),
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({ thunk: false, serializableCheck: false })
        .concat(promiseMiddleware)
        .concat(sagaMiddleware)
        .concat(routerMiddleware),
    // This is redux log will output in console log that include action.....
    // .concat(logger),
  })

  sagaMiddleware.run(genRootSaga(effectMain))

  return store
}

export const store = makeStore()

export const history = createReduxHistory(store)

export const persistor = persistStore(store)
